import { useEffect, useRef, type CSSProperties } from 'react'
import { Icon } from '@iconify/react'
import type { CalendarAttendance, LandingAchievement } from '@/lib/landing-achievements'
import styles from './LandingCalendarModal.module.css'

export function LandingCalendarModal({
  state,
  achievement,
  onClose,
  onCompleteProfile,
  onViewPrizes,
}: {
  state: CalendarAttendance
  achievement: LandingAchievement
  onClose: () => void
  onCompleteProfile: () => void
  onViewPrizes: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const currentDay = useRef<HTMLLIElement>(null)
  const completed = achievement.unlocked
  const target = Math.max(1, Math.min(365, state.target_days))
  const count = Math.min(state.consecutive_days, target)
  const rewards = achievement.rewards
  const hasRewards = Boolean(
    rewards?.coupon_percent || rewards?.lucky_numbers || rewards?.roulette_spins
  )
  // Keep long challenges compact, opening on the week that contains today's stamp.
  useEffect(() => {
    const element = dialog.current
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    element?.showModal()
    currentDay.current?.scrollIntoView?.({ block: 'nearest', behavior: 'instant' })
    return () => {
      element?.close()
      previousFocus?.focus()
    }
  }, [])

  return (
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-labelledby="calendar-title"
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className={styles.body}>
        <button
          className={styles.close}
          type="button"
          onClick={onClose}
          aria-label="Fechar calendário"
        >
          <Icon icon="mdi:close" />
        </button>
        <header className={styles.header}>
          <span className={styles.eyebrow}>
            <Icon icon="mdi:sparkles" aria-hidden /> SUA PRESENÇA VALE OURO
          </span>
          <div className={styles.artwork} aria-hidden>
            <span className={styles.orbit} />
            <img src="/conquistas%20doa%C3%A7%C3%B5es/23%20calendario.png" alt="" />
            <span className={styles.sparkOne}>✦</span>
            <span className={styles.sparkTwo}>✧</span>
          </div>
          <h2 id="calendar-title">
            {completed
              ? (achievement.content?.title ?? 'Uma sequência para celebrar!')
              : state.eligible
                ? 'Cada dia, mais perto.'
                : 'Sua jornada começa aqui.'}
          </h2>
          <p>
            {completed
              ? 'Você completou o desafio e conquistou o selo Calendário!'
              : state.eligible
                ? 'Um pequeno encontro diário. Uma conquista especial esperando por você.'
                : 'Complete seu perfil para começar a marcar seus dias e conquistar esta recompensa.'}
          </p>
        </header>

        <section className={styles.calendar} aria-label="Seu calendário de presenças">
          <div className={styles.calendarTop}>
            <span>
              <Icon icon="mdi:calendar-heart" aria-hidden />{' '}
              {completed ? 'Missão cumprida' : 'Sua sequência'}
            </span>
            <strong>
              {count}
              <small>
                {' '}
                / {target} {target === 1 ? 'dia' : 'dias'}
              </small>
            </strong>
          </div>
          <div
            className={styles.progress}
            role="progressbar"
            aria-label="Dias consecutivos"
            aria-valuemin={0}
            aria-valuemax={target}
            aria-valuenow={count}
          >
            <span
              style={{ '--progress': `${(count / target) * 100}%` } as CSSProperties}
            />
          </div>
          <ol className={styles.days}>
            {Array.from({ length: target }, (_, index) => {
              const day = index + 1
              const checked = day <= count
              const today = day === count && state.checked_in_today
              const date =
                state.started_on && checked
                  ? new Date(`${state.started_on}T12:00:00Z`)
                  : null
              if (date) date.setUTCDate(date.getUTCDate() + index)
              const dateLabel = date?.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                timeZone: 'UTC',
              })
              return (
                <li
                  key={day}
                  ref={day === count ? currentDay : undefined}
                  className={`${styles.day} ${checked ? styles.checked : ''} ${today ? styles.today : ''} ${day === target ? styles.finalDay : ''}`}
                  style={{ '--delay': `${Math.min(index, 13) * 45}ms` } as CSSProperties}
                  aria-label={`Dia ${day}${dateLabel ? `, ${dateLabel}` : ''}: ${checked ? 'presença confirmada' : 'pendente'}`}
                >
                  <span className={styles.dayLabel}>DIA {day}</span>
                  <span className={styles.stamp} aria-hidden>
                    <Icon
                      icon={
                        checked
                          ? 'mdi:check-bold'
                          : day === target
                            ? 'mdi:gift-outline'
                            : 'mdi:circle-small'
                      }
                    />
                  </span>
                  <small>
                    {today
                      ? 'Hoje'
                      : (dateLabel ?? (day === target ? 'Conquista' : 'Em breve'))}
                  </small>
                </li>
              )
            })}
          </ol>
          <p className={styles.status} role="status">
            <Icon
              icon={completed ? 'mdi:check-decagram' : 'mdi:heart-outline'}
              aria-hidden
            />
            {completed
              ? 'Selo conquistado. Essa vitória é sua!'
              : state.checked_in_today
                ? 'Presença de hoje confirmada. Até amanhã!'
                : state.eligible
                  ? 'Volte ao seu perfil todos os dias para continuar.'
                  : 'Seu primeiro dia conta com o perfil 100% completo.'}
          </p>
        </section>

        <section className={styles.rewards} aria-label="Recompensas do calendário">
          <span className={styles.giftIcon}>
            <Icon icon="mdi:gift-open-outline" aria-hidden />
          </span>
          <div>
            <h3>{completed ? 'Você conquistou' : 'No final da sua sequência'}</h3>
            <p>
              {completed
                ? (achievement.content?.text ??
                  'O selo Calendário agora faz parte da sua coleção.')
                : achievement.locked_reason?.trim() ||
                  'Um selo especial para celebrar sua presença nesta campanha.'}
            </p>
            <ul>
              <li>
                <Icon icon="mdi:medal-outline" aria-hidden /> Selo Calendário
              </li>
              {rewards?.coupon_percent ? (
                <li>
                  <Icon icon="mdi:ticket-percent-outline" aria-hidden /> Cupom de{' '}
                  {rewards.coupon_percent}%
                </li>
              ) : null}
              {rewards && rewards.lucky_numbers > 0 ? (
                <li>
                  <Icon icon="mdi:ticket-confirmation-outline" aria-hidden />{' '}
                  {rewards.lucky_numbers} números extras para resgatar
                </li>
              ) : null}
              {rewards && rewards.roulette_spins > 0 ? (
                <li>
                  <Icon icon="mdi:ferris-wheel" aria-hidden /> {rewards.roulette_spins}{' '}
                  giros extras na roleta
                </li>
              ) : null}
              {!completed && (
                <li>
                  <Icon icon="mdi:email-heart-outline" aria-hidden /> Uma mensagem
                  especial ao conquistar
                </li>
              )}
            </ul>
          </div>
        </section>
        {!completed && (
          <div className={styles.rules}>
            <Icon icon="mdi:information-outline" aria-hidden />
            <p>
              Acesse sua conta nesta campanha por{' '}
              <strong>
                {target} {target === 1 ? 'dia' : 'dias'} {target === 1 ? '' : 'seguidos'}
              </strong>
              , com o perfil completo. Cada dia conta uma vez, pelo horário de Brasília.
              Se faltar um dia, a sequência recomeça. O selo e os prêmios são seus uma
              única vez.
            </p>
          </div>
        )}
        {completed && achievement.content?.video_url && (
          <div className={styles.video}>
            <iframe
              src={achievement.content.video_url}
              title="Vídeo da conquista Calendário"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              sandbox="allow-scripts allow-same-origin"
              allow="encrypted-media"
            />
          </div>
        )}
        <button
          type="button"
          className={styles.primary}
          onClick={() => {
            onClose()
            if (!state.eligible && !completed) onCompleteProfile()
            else if (completed && hasRewards) onViewPrizes()
          }}
        >
          {completed
            ? hasRewards
              ? 'Ver minhas recompensas'
              : 'Celebrar minha conquista'
            : state.eligible
              ? 'Combinado, até amanhã!'
              : 'Completar meu perfil'}
          <Icon icon="mdi:arrow-right" aria-hidden />
        </button>
        <span className={styles.footnote}>
          {completed
            ? 'Uma conquista para guardar na sua coleção.'
            : 'Seu progresso fica guardado. Um dia de cada vez.'}
        </span>
      </div>
    </dialog>
  )
}
